import { Box } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';
import React from 'react';
import { Link } from 'react-router-dom';
import { translate } from '../../../../../common/intl';
import { MomentFormats, useDate } from '../../../../../common/utils/date';
import { DocumentRevision, SmartReferenceType } from '../../../../../state/ducks/documentRevisions/types';
import { documentVersionPath } from '../../../../document.revision/utils/paths';
import useStyles from './LHRDeviation.banner.styles';

interface Props {
  documentRevision: DocumentRevision
}

const LHRDeviationBanner: React.FC<Props> = ({ documentRevision }) => {
  const classes = useStyles();
  const { formatDisplayDate } = useDate();

  // LHR steps carry the deviation refs on their parent LHR
  const parentLHR = documentRevision?.smartReferencesTo?.find(
    ref => ref.active && ref.type === SmartReferenceType.HiddenPiInstance,
  )?.toDocRev;
  const deviationRef = [
    ...(documentRevision?.smartReferencesFrom ?? []),
    ...(parentLHR?.smartReferencesFrom ?? []),
  ].find(ref => ref.active && ref.type === SmartReferenceType.DeviationAffectedLHR);

  // A step started before the deviation was released doesn't get it, so its schema carries no deviation stamp
  const isStepWithoutDeviation = Boolean(parentLHR)
    && !documentRevision?.schema?.some(item => !item.deleted && item.editorConfig?.deviation);

  if (!deviationRef || isStepWithoutDeviation) {
    return null;
  }

  const { effectivityDateFrom, effectivityDateTo } = deviationRef.metadata;
  const fromDate = formatDisplayDate(effectivityDateFrom, MomentFormats.BriefDate) ?? '';
  const toDate = formatDisplayDate(effectivityDateTo, MomentFormats.BriefDate) ?? '';

  const deviationDocRev = deviationRef.fromDocRev;
  const linkUrl = deviationDocRev?.id && deviationDocRev?.documentId
    ? documentVersionPath(deviationDocRev.id, deviationDocRev.documentId)
    : undefined;

  return (
    <Box className={classes.root}>
      <span>{translate('lhrDeviation.banner.applied')} </span>
      <span className={classes.regular}>
        {'\u00A0'}{translate('lhrDeviation.banner.dates', { fromDate, toDate })}
      </span>
      {linkUrl && (
        <Link to={linkUrl} target="_blank" className={classes.linkBox}>
          <OpenInNew className={classes.icon} />
        </Link>
      )}
    </Box>
  );
};

export default LHRDeviationBanner;
